import { FormikProps } from "formik";
import { FC, ReactNode, useRef } from "react";
import { WorkSearchSortForm } from "../../../../../domain/models/dto/WorkDTO";
import { WorkSortingFields } from "../../../../../domain/enums/entity-fields/WorkSortingFields";
import { useTranslation } from "react-i18next";
import CustomPopup from "../../../../../custom-components/CustomPopup";
import { SortingOrder } from "../../../../../domain/enums/common/SortingOrderEnum";
import CheckMarkIconSVG from "../../../svg/CheckMarkIconSVG";
import { useClickOutOfElement } from "../../../../../custom-hooks/base-hooks/useClickOutOfElement";

interface WorkSortingPopupProps {
    form: FormikProps<WorkSearchSortForm>;
    sortingType: WorkSortingFields;
    currentSortingType: WorkSortingFields;
    setSortingType: (value: WorkSortingFields) => void;
    openingHandler: (value: WorkSortingFields) => void;
    buttonElement: ReactNode;
}

const WorkSortingPopup: FC<WorkSortingPopupProps> = (props) => {
    const { t } = useTranslation();
    const tooltipRef = useRef(null);
    const sortingHandler = async (sort: WorkSortingFields, sortingOrder: SortingOrder) => {
        await props.form.setFieldValue(sort, sortingOrder);
        await props.form.submitForm();
        props.setSortingType(null);
    };
    const activeTooltip = props.sortingType === props.currentSortingType;
    const isDefault = props.form.values[props.sortingType] === SortingOrder.DEFAULT;
    const isAsc = props.form.values[props.sortingType] === SortingOrder.ASC;
    const isDesc = props.form.values[props.sortingType] === SortingOrder.DESC;

    const closePopupHandler = () => {
        activeTooltip && props.setSortingType(null);
    };

    useClickOutOfElement(tooltipRef, closePopupHandler);
    const sortingTypeLabel = t(`catalog.sort.${props.sortingType}`).toLowerCase();

    return (
        <CustomPopup
            isVisible={activeTooltip}
            setIsVisible={closePopupHandler}
            wrapClassName="bg-backgroundInput w-[270px] h-[178px]"
            buttonElement={
                <div
                    onClick={() => props.openingHandler(props.sortingType)}
                    className={`relative flex items-center cursor-pointer group ${
                        isAsc || isDesc ? "*:text-textWhite" : ""
                    }`}
                >
                    {props.buttonElement}
                </div>
            }
            closingCrossClassName="hidden"
        >
            <div className="relative flex flex-col w-full h-full">
                <h2 className="H16B text-textWhite text-center mb-4">
                    {t("catalog.sort.popupLabel", { sortingType: sortingTypeLabel })}
                </h2>
                <div className="flex flex-col gap-3 dmax2:gap-2 items-center justify-center">
                    <div
                        className={`flex justify-center H16 dmax2:H14 text-textWhite text-center w-[214px] h-[30px] hover:font-[500] border-b border-b-textFaded cursor-pointer`}
                        onClick={() => sortingHandler(props.sortingType, SortingOrder.DEFAULT)}
                    >
                        {t(`sortingOrder.${SortingOrder.DEFAULT}`)}
                        {isDefault && <CheckMarkIconSVG className="ml-2.5" />}
                    </div>
                    <div
                        className={`flex justify-center H16 dmax2:H14 text-textWhite text-center w-[214px] h-[30px] hover:font-[500] border-b border-b-textFaded cursor-pointer`}
                        onClick={() => sortingHandler(props.sortingType, SortingOrder.DESC)}
                    >
                        {t(`catalog.sort.order.${props.sortingType}.${SortingOrder.DESC}`)}
                        {isDesc && <CheckMarkIconSVG className="ml-2.5" />}
                    </div>
                    <div
                        className={`flex justify-center H16 dmax2:H14 text-textWhite text-center w-[214px] h-[30px] hover:font-[500] cursor-pointer`}
                        onClick={() => sortingHandler(props.sortingType, SortingOrder.ASC)}
                    >
                        {t(`catalog.sort.order.${props.sortingType}.${SortingOrder.ASC}`)}
                        {isAsc && <CheckMarkIconSVG className="ml-2.5" />}
                    </div>
                </div>
            </div>
        </CustomPopup>
    );
};

export default WorkSortingPopup;
